import { ColumnDef, createColumnHelper, Row } from '@tanstack/react-table';
import { DataTable } from 'components/table/DataTable';
import { useModelService } from 'services/model/model.service';
import { useIntl } from 'react-intl';
import { DataTableContextProvider } from 'components/table/context/DataTableContextProvider';
import { useEntriesDemandReportData } from 'pages/insights-pro/performance-tab/reports/demand-monitoring/queryHooks';
import { useContext, useEffect, useState } from 'react';
import { TenantConfigContext } from 'context/TenantConfigContext';
import {
  buildStandardColumns,
  createCellExportOptions,
  DemandTableProps,
  DemandTableRow,
  digestData,
} from 'pages/insights-pro/performance-tab/reports/demand-monitoring/DemandTableHelpers';
import { ProductDetailsTableRow } from 'pages/insights-pro/performance-tab/reports/demand-monitoring/tables/ProductDetailsTableRow';
import { useNavigate } from 'react-router-dom';
import { DEMAND_MONITORING_ROOT } from 'pages/insights-pro/performance-tab/reports/demand-monitoring/DemandHome';

interface DemandCountryTableRow extends DemandTableRow {
  chnCountry: string;
}

export function CountryTable({ merchantData, filters }: DemandTableProps) {
  const intl = useIntl();
  const navigate = useNavigate();

  const { getMeasureByMetric, getAttributes } = useModelService();
  const [chnCountry] = getAttributes(['chnCountry']);
  const ordGmvVal = getMeasureByMetric('ordGmvVal', 'OD');
  const ordQty = getMeasureByMetric('ordQty', 'OD');

  const columnHelper = createColumnHelper<DemandCountryTableRow>();

  const defaultColumns = buildStandardColumns(ordQty, intl);

  const buildColumns = () => [
    columnHelper.accessor('chnCountry', {
      id: 'chnCountry',
      header: () => intl.formatMessage({ id: 'entities.name.singular.country' }),
      cell: info => intl.formatDisplayName(info.getValue(), { type: 'region' }),
    }),
  ];

  const cols: ColumnDef<DemandCountryTableRow>[] = (buildColumns() as ColumnDef<ProductDetailsTableRow>[]).concat(
    defaultColumns as ColumnDef<ProductDetailsTableRow>[]
  ) as ColumnDef<DemandCountryTableRow>[];

  const { data } = useEntriesDemandReportData(chnCountry.code, filters);

  const { currentDataVersion } = useContext(TenantConfigContext);

  const [tableRows, setTableRows] = useState<DemandCountryTableRow[]>([]);

  useEffect(() => {
    if (currentDataVersion) {
      digestData(
        merchantData,
        data,
        chnCountry,
        ordGmvVal,
        ordQty,
        entry => ({
          chnCountry: chnCountry.getRawValue(entry.attributes),
        }),
        setTableRows
      );
    }
  }, [data, merchantData, currentDataVersion]);

  const showDetails = (row: Row<DemandCountryTableRow>) => {
    navigate(`${DEMAND_MONITORING_ROOT}/detail/${chnCountry.code}/${row.original.chnCountry}${location.search}`);
  };

  return (
    <>
      <DataTableContextProvider>
        <DataTable
          rows={tableRows}
          onRowClick={showDetails}
          columns={cols}
          titleId="insights.country"
          defaultSortingState={[{ id: 'ordGmvVal', desc: true }]}
          exportConfigOptions={createCellExportOptions('Countries')}
        />
      </DataTableContextProvider>
    </>
  );
}
